import { useEffect, useMemo, useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { ArrowRight, Users } from "lucide-react"
import { useActiveEvent } from "@/core/active-event"
import {
  eventSettingsQueryOptions,
  patchEvent,
  putRsvpCheckboxes,
  type EventSettingsRow,
} from "@/core/settings"
import {
  configFromDraft,
  describeRsvpSaveError,
  draftFromConfig,
  parseRsvpCheckboxConfig,
  validateRsvpCheckboxes,
  type RsvpCheckboxesDraft,
  type RsvpSaveError,
} from "@/core/rsvp-checkboxes"
import { guestsQueryOptions } from "@/core/people"
import { publicEventSiteUrl } from "@/core/event-site"
import { ApiError } from "@/core/api"
import {
  Card,
  CopyLinkBox,
  Field,
  PrimaryButton,
  Select,
  SectionPage,
  SectionPageFooter,
  TextInput,
} from "@/components/settings/primitives"
import { DateTimePicker } from "@/components/ui/DateTimePicker"
import { RsvpCheckboxesCard, useRsvpDocIndex } from "@/components/settings/RsvpCheckboxesCard"
import { cn } from "@/core/utils"

export const Route = createFileRoute("/$orgSlug/$eventSlug/settings/registration")({
  component: RegistrationView,
})

type Method = "invite_only" | "public_page" | "external"

const METHODS: { value: Method; label: string; blurb: string }[] = [
  { value: "invite_only", label: "Invite-only", blurb: "Guests join by email invitation." },
  { value: "public_page", label: "Public page", blurb: "Anyone with the link can register." },
  { value: "external", label: "External", blurb: "Registration handled on another platform." },
]

type FormState = {
  registrationMethod: Method
  publicRegistrationMode: "request" | "auto-confirm"
  maxCapacity: string
  rsvpDeadline: string
}

function RegistrationView() {
  const { eventId, orgSlug, eventSlug, row: eventRow } = useActiveEvent()
  const eventStart = eventRow?.startsAt ? eventRow.startsAt.slice(0, 10) : undefined
  const eventEnd = eventRow?.endsAt ? eventRow.endsAt.slice(0, 10) : undefined
  const qc = useQueryClient()
  const q = useQuery({ ...eventSettingsQueryOptions(eventId ?? ""), enabled: !!eventId })

  const [form, setForm] = useState<FormState | null>(null)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (!q.data || dirty) return
    setForm({
      registrationMethod: q.data.registrationMethod ?? "invite_only",
      publicRegistrationMode: q.data.publicRegistrationMode ?? "request",
      maxCapacity: q.data.maxCapacity != null ? String(q.data.maxCapacity) : "",
      rsvpDeadline: q.data.rsvpDeadline ?? "",
    })
  }, [q.data, dirty])

  const save = useMutation({
    mutationFn: () => {
      if (!eventId || !form) throw new Error("Not ready")
      const patch: Partial<EventSettingsRow> = {
        registrationMethod: form.registrationMethod,
        publicRegistrationEnabled: form.registrationMethod === "public_page" ? 1 : 0,
        publicRegistrationMode: form.publicRegistrationMode,
        maxCapacity: form.maxCapacity.trim() === "" ? null : Number(form.maxCapacity),
        rsvpDeadline: form.rsvpDeadline.trim() === "" ? null : form.rsvpDeadline,
      }
      return patchEvent(eventId, patch)
    },
    onSuccess: () => {
      setDirty(false)
      toast.success("Registration settings saved")
      void qc.invalidateQueries({ queryKey: ["resource", "events", eventId] })
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Failed to save registration"),
  })

  const update = (patch: Partial<FormState>) => {
    setDirty(true)
    setForm((f) => (f ? { ...f, ...patch } : f))
  }

  // RSVP checkboxes: their own draft + endpoint (the column is protected from
  // PATCH), saved by the same footer button.
  const docIndex = useRsvpDocIndex(eventId)
  const [rsvp, setRsvp] = useState<RsvpCheckboxesDraft | null>(null)
  const [rsvpDirty, setRsvpDirty] = useState(false)
  const [rsvpAttempted, setRsvpAttempted] = useState(false)
  const [rsvpError, setRsvpError] = useState<RsvpSaveError | null>(null)

  useEffect(() => {
    if (!q.data || rsvpDirty) return
    setRsvp(draftFromConfig(parseRsvpCheckboxConfig(q.data.rsvpCheckboxes)))
  }, [q.data, rsvpDirty])

  const rsvpValidation = useMemo(
    () => (rsvp ? validateRsvpCheckboxes(rsvp, docIndex) : null),
    [rsvp, docIndex],
  )

  const saveRsvp = useMutation({
    mutationFn: () => {
      if (!eventId || !rsvp) throw new Error("Not ready")
      return putRsvpCheckboxes(eventId, configFromDraft(rsvp))
    },
    onSuccess: (res) => {
      // Adopt the saved config so new rows keep the ids the server stored.
      if (res.config) setRsvp(draftFromConfig(res.config))
      setRsvpDirty(false)
      setRsvpAttempted(false)
      setRsvpError(null)
      toast.success("RSVP checkboxes saved")
      void qc.invalidateQueries({ queryKey: ["resource", "events", eventId] })
    },
    onError: (e) => {
      const err =
        e instanceof ApiError
          ? describeRsvpSaveError(e.code, e.body, e.message, (id) => docIndex?.get(id)?.title)
          : { message: "Failed to save RSVP checkboxes" }
      setRsvpError(err)
      toast.error(err.message)
    },
  })

  const updateRsvp = (next: RsvpCheckboxesDraft) => {
    setRsvpDirty(true)
    setRsvpError(null)
    setRsvp(next)
  }

  const saveAll = () => {
    if (rsvpDirty) {
      setRsvpAttempted(true)
      if (rsvpValidation && !rsvpValidation.ok) {
        toast.error("Fix the highlighted RSVP checkbox issues first")
        return
      }
      saveRsvp.mutate()
    }
    if (dirty) save.mutate()
  }
  const saving = save.isPending || saveRsvp.isPending

  return (
    <SectionPage
      title="Registration"
      description="How guests get onto the list, and the limits that gate it."
    >
      {q.isError && (
        <Card>
          <p className="text-[13px] text-rose-600 dark:text-rose-400">
            {(q.error as Error)?.message ?? "Failed to load settings"}
          </p>
        </Card>
      )}

      <Card title="Registration method">
        <div className="grid gap-2.5 @[560px]:grid-cols-3">
          {METHODS.map((m) => {
            const active = form?.registrationMethod === m.value
            return (
              <button
                key={m.value}
                type="button"
                disabled={!form}
                onClick={() => update({ registrationMethod: m.value })}
                className={cn(
                  "rounded-xl border px-3.5 py-3 text-left transition disabled:opacity-50",
                  active
                    ? "border-[var(--accent)] bg-[var(--accent)]/8 ring-1 ring-[var(--accent)]/30"
                    : "border-plum-200/60 hover:border-plum-300/70 dark:border-white/10 dark:hover:border-white/20",
                )}
              >
                <span className="block text-[13px] font-semibold text-plum-900 dark:text-plum-50">
                  {m.label}
                </span>
                <span className="mt-0.5 block text-[12px] text-plum-700/55 dark:text-plum-200/40">
                  {m.blurb}
                </span>
              </button>
            )
          })}
        </div>

        {form?.registrationMethod === "public_page" && (
          <Field
            label="When someone registers"
            hint="Hold for approval, or auto-confirm seats until capacity is reached."
          >
            <Select
              value={form.publicRegistrationMode}
              onChange={(e) =>
                update({ publicRegistrationMode: e.target.value as "request" | "auto-confirm" })
              }
            >
              <option value="request">Hold for approval</option>
              <option value="auto-confirm">Auto-confirm until full</option>
            </Select>
          </Field>
        )}

        {form?.registrationMethod === "public_page" && orgSlug && eventSlug && (
          <Field
            label="Registration link"
            hint="The public event site. Anyone with this link can register."
          >
            <CopyLinkBox
              url={publicEventSiteUrl(orgSlug, eventSlug)}
              ariaLabel="Copy registration link"
            />
          </Field>
        )}
      </Card>

      <Card title="Limits">
        <Field label="Max capacity" hint="Leave blank for no cap.">
          <TextInput
            type="number"
            min={0}
            inputMode="numeric"
            placeholder="Unlimited"
            value={form?.maxCapacity ?? ""}
            disabled={!form}
            onChange={(e) => update({ maxCapacity: e.target.value })}
          />
        </Field>
        <Field
          label="RSVP deadline"
          hint="Event-local date & time. Leave blank for no deadline."
        >
          <DateTimePicker
            mode="datetime"
            value={form?.rsvpDeadline ?? ""}
            disabled={!form}
            onChange={(v) => update({ rsvpDeadline: v })}
            eventStart={eventStart}
            eventEnd={eventEnd}
            placeholder="No deadline"
            ariaLabel="RSVP deadline"
          />
        </Field>
      </Card>

      <RsvpCheckboxesCard
        eventId={eventId ?? null}
        draft={rsvp}
        onChange={updateRsvp}
        docs={docIndex}
        validation={rsvpValidation}
        attempted={rsvpAttempted}
        serverError={rsvpError}
      />

      <PendingApprovalCard
        eventId={eventId ?? null}
        orgSlug={orgSlug ?? ""}
        eventSlug={eventSlug ?? ""}
      />

      <SectionPageFooter>
        <PrimaryButton onClick={saveAll} disabled={(!dirty && !rsvpDirty) || saving}>
          {saving ? "Saving…" : "Save changes"}
        </PrimaryButton>
      </SectionPageFooter>
    </SectionPage>
  )
}

// ── pending-approval pointer ─────────────────────────────────────────────────
// Approvals are day-to-day ops, so the queue itself lives on the Guests page
// (pending view, with the full lifecycle actions). This card is just the
// signpost + count so the setting and the work stay linked.

function PendingApprovalCard({
  eventId,
  orgSlug,
  eventSlug,
}: {
  eventId: string | null
  orgSlug: string
  eventSlug: string
}) {
  const q = useQuery({ ...guestsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const pending = (q.data ?? []).filter((g) => g.status === "pending")

  return (
    <Card title="Pending approval" description="Public registrants waiting on a decision.">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[13px] text-plum-800 dark:text-plum-100">
          <Users className="h-4 w-4 text-plum-700/55 dark:text-plum-200/40" />
          {q.isLoading ? (
            "Loading…"
          ) : pending.length === 0 ? (
            <span className="text-plum-700/55 dark:text-plum-200/40">
              No one is waiting on approval right now.
            </span>
          ) : (
            <span>
              <span className="font-semibold">{pending.length}</span>{" "}
              {pending.length === 1 ? "person is" : "people are"} waiting on approval.
            </span>
          )}
        </div>
        <Link
          to="/$orgSlug/$eventSlug/people"
          params={{ orgSlug, eventSlug }}
          search={{ view: "pending" }}
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--accent)] hover:underline"
        >
          Review in Guests <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </Card>
  )
}
